import { useQuery } from '@tanstack/react-query';
import type { MyTeam, TeamMember, TeamOpenCounts, TeamPostList } from '@up2d8/contracts';
import { useSession } from '../../shared/session';

/**
 * De gegevens van het scherm Mijn team (Stap 17, TEAM-2).
 *
 * Alles hier draait om één regel: er is **geen cache van de teamgegevens**. De spec zegt bij
 * "offline": een volledig scherm, niets uit de cache. Dat is geen technische beperking maar een
 * keuze over bewijs — "zeven van de negen bevestigd" van gisteren is erger dan geen antwoord, want
 * de ploegbaas handelt ernaar.
 *
 * Daarom staat `staleTime: 0` op elke query hier, en gebruikt geen enkele de offline-wachtrij.
 */

/** Hoe lang een antwoord bruikbaar blijft. Nul: dit scherm kijkt altijd opnieuw. */
const VERS = 0;

export function useMyTeam(options: { subgroups?: boolean } = {}) {
  const { api, has } = useSession();
  const subgroups = options.subgroups ?? false;

  return useQuery({
    queryKey: ['my-team', subgroups],
    enabled: has('directory.team.view'),
    staleTime: VERS,
    queryFn: async (): Promise<MyTeam> => {
      const { data, error } = await api.GET('/api/v1/me/team', {
        params: { query: { subgroups } } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as MyTeam;
    },
  });
}

export function useTeamPosts(teamId: string | null) {
  const { api, has } = useSession();

  return useQuery({
    queryKey: ['my-team-posts', teamId],
    enabled: has('directory.team.view'),
    staleTime: VERS,
    queryFn: async (): Promise<TeamPostList> => {
      const { data, error } = await api.GET('/api/v1/me/team/posts', {
        params: { query: teamId ? { teamId } : {} } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as TeamPostList;
    },
  });
}

/** Per teamlid hoeveel verplichte berichten openstaan; voor de badge "2 open". */
export function useTeamOpenCounts(teamId: string | null) {
  const { api, has } = useSession();

  return useQuery({
    queryKey: ['my-team-open-counts', teamId],
    enabled: has('directory.team.view'),
    staleTime: VERS,
    queryFn: async (): Promise<TeamOpenCounts> => {
      const { data, error } = await api.GET('/api/v1/me/team/open-counts', {
        params: { query: teamId ? { teamId } : {} } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as TeamOpenCounts;
    },
  });
}

/**
 * Heeft deze gebruiker een team, en hoeveel staat er open?
 *
 * Voor de ingangen: het profielmenu, de kaart op Home en de zijbalk. Wie geen team heeft, ziet
 * **geen enkele** ingang (spec §2). Een lege ingang zou erger zijn dan geen: hij belooft een
 * scherm dat niets zegt, en de medewerker gaat zoeken naar wat hij fout doet.
 *
 * Het aantal in de kaart is het aantal **berichten** dat nog niet door iedereen bevestigd is, niet
 * het aantal mensen: "2 nog te bevestigen" naast een lijst van twee berichten leest hetzelfde, en
 * een kaart die over mensen gaat zou de namen suggereren die er misschien niet mogen staan.
 */
export function useHasTeam(): { hasTeam: boolean; openPosts: number; loading: boolean } {
  const { has } = useSession();
  const team = useMyTeam();
  const posts = useTeamPosts(null);

  const leden = (team.data?.teams ?? []).reduce(
    (totaal, groep) => totaal + groep.members.length,
    0,
  );
  const open = (posts.data?.items ?? []).filter((post) => post.acknowledged < post.expected).length;

  return {
    hasTeam: has('directory.team.view') && leden > 0,
    openPosts: open,
    loading: team.isPending || posts.isPending,
  };
}

/** De leden van het gekozen team, actieve eerst en de gedeactiveerde onderaan (spec §4). */
export function membersOfTeam(
  team: MyTeam | undefined,
  teamId: string | null,
): { active: TeamMember[]; deactivated: TeamMember[] } {
  const groepen = team?.teams ?? [];
  const gekozen = teamId ? groepen.filter((groep) => groep.teamId === teamId) : groepen;

  // Zonder keuze alle teams samen, zonder dubbels: Ana kan in de rapporteringslijn én in een
  // beheerde groep zitten, en dan hoort ze één rij te zijn.
  const actief = new Map<string, TeamMember>();
  const uit = new Map<string, TeamMember>();
  for (const groep of gekozen) {
    for (const lid of groep.members) actief.set(lid.personId, lid);
    for (const lid of groep.deactivatedMembers) uit.set(lid.personId, lid);
  }
  for (const id of actief.keys()) uit.delete(id);

  const opNaam = (links: TeamMember, rechts: TeamMember) =>
    links.displayName.localeCompare(rechts.displayName, 'nl');
  return {
    active: [...actief.values()].sort(opNaam),
    deactivated: [...uit.values()].sort(opNaam),
  };
}

/** Vanaf hoeveel leden er een zoekveld boven de lijst komt (spec §2, frame 1). */
export const TEAM_SEARCH_FROM = 10;
